import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { portfolio, getPortfolioBySlug } from "../data/portfolio";
import useReveal from "../hooks/useReveal";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function PortfolioDetail() {
  useReveal();
  const { slug } = useParams();
  const item = getPortfolioBySlug(slug);

  if (!item) {
    return (
      <main className="min-h-screen bg-[#F7F5F0] text-[#1C1C19] flex flex-col">
        <Navbar />
        <section className="flex-1 flex items-center justify-center px-6 py-40">
          <div className="text-center" data-testid="portfolio-not-found">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5C58]">
              404 · Not found
            </span>
            <h1 className="mt-6 font-display text-5xl md:text-7xl font-light tracking-tight">
              We don't have <span className="italic">this one.</span>
            </h1>
            <Link
              to="/"
              data-testid="back-home-link"
              className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1C1C19] text-[#F7F5F0]"
            >
              <ArrowLeft size={16} />
              Back home
            </Link>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  const idx = portfolio.findIndex((p) => p.slug === item.slug);
  const next = portfolio[(idx + 1) % portfolio.length];

  return (
    <main className="min-h-screen bg-[#F7F5F0] text-[#1C1C19] overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section
        data-testid="portfolio-detail-hero"
        className="relative pt-32 md:pt-40 pb-16 md:pb-24"
      >
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <Link
            to="/#portfolio"
            data-testid="back-to-portfolio"
            className="link-underline inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#5C5C58]"
          >
            <ArrowLeft size={14} /> All work
          </Link>

          <div className="mt-10 grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-9">
              <span
                className="inline-block px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.25em] font-bold"
                style={{
                  backgroundColor: item.accent,
                  color: item.accent === "#F2C14E" ? "#1C1C19" : "#F7F5F0",
                }}
              >
                {item.sector}
              </span>
              <h1
                data-testid="portfolio-detail-name"
                className="mt-6 font-display text-5xl sm:text-6xl lg:text-8xl font-light tracking-tight leading-[0.98]"
              >
                {item.engagementType}
              </h1>
            </div>
            <div className="md:col-span-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C5C58]">
                Current stage
              </div>
              <div
                className="mt-2 font-display text-2xl font-light leading-snug"
                data-testid="portfolio-detail-stage"
              >
                {item.currentStage}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cover */}
      <section className="relative">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <div className="aspect-[16/9] rounded-3xl overflow-hidden bg-[#E6E3DB]">
            <img
              src={item.cover}
              alt={item.sector}
              className="w-full h-full object-cover"
              data-testid="portfolio-detail-cover"
            />
          </div>
        </div>
      </section>

      {/* Problem + Approach */}
      <section className="py-24 md:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5 reveal">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5C58]">
              (01) · The problem
            </span>
            <h2 className="mt-6 font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.1]">
              Where the work
              <br /> <span className="italic">began.</span>
            </h2>
            <p
              className="mt-8 text-lg font-light leading-relaxed text-[#1C1C19]/85"
              data-testid="portfolio-detail-problem"
            >
              {item.problem}
            </p>
          </div>
          <div className="md:col-span-6 md:col-start-7 reveal">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5C58]">
              (02) · Our approach
            </span>
            <h2 className="mt-6 font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.1]">
              How we're
              <br /> <span className="italic">building it.</span>
            </h2>
            <p
              className="mt-8 text-lg font-light leading-relaxed text-[#1C1C19]/85"
              data-testid="portfolio-detail-approach"
            >
              {item.approach}
            </p>
          </div>
        </div>
      </section>

      {/* Current stage band */}
      <section
        className="py-24 md:py-32"
        style={{
          background: item.accent,
          color: item.accent === "#F2C14E" ? "#1C1C19" : "#F7F5F0",
        }}
      >
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.25em] opacity-80">
            (03) · Where it stands
          </span>
          <h2
            className="mt-6 font-display text-4xl md:text-7xl font-light tracking-tight leading-[1.05] max-w-5xl"
            data-testid="portfolio-detail-stage-headline"
          >
            {item.currentStage}
          </h2>
        </div>
      </section>

      {/* Next case */}
      <section className="py-24 md:py-32 bg-[#1C1C19] text-[#F7F5F0]">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#F2C14E]">
            Next engagement
          </span>
          <Link
            to={`/portfolio/${next.slug}`}
            data-testid="next-portfolio-link"
            className="mt-6 group flex flex-col md:flex-row md:items-end md:justify-between gap-6"
          >
            <h3 className="font-display text-4xl md:text-7xl font-light tracking-tight leading-[1] group-hover:text-[#F2C14E] transition-colors duration-500 max-w-3xl">
              {next.engagementType}
              <span className="block text-[#F7F5F0]/60 text-2xl md:text-3xl mt-3 not-italic">
                {next.sector}
              </span>
            </h3>
            <span className="inline-flex items-center gap-3 px-6 py-3 rounded-full border border-[#F7F5F0]/40 text-xs uppercase tracking-widest group-hover:bg-[#F7F5F0] group-hover:text-[#1C1C19] transition-colors duration-500">
              Read next
              <ArrowRight size={14} />
            </span>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#E26D5C] text-[#F7F5F0] relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-4xl md:text-7xl font-light tracking-tight leading-[1.05]">
            Building something
            <br /> <span className="italic">worth backing?</span>
          </h2>
          <Link
            to="/#contact"
            data-testid="portfolio-cta"
            className="btn-magnetic mt-10 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#1C1C19] text-[#F7F5F0] text-sm uppercase tracking-widest"
          >
            Pitch us
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
